import React from 'react'
import { cn } from '@/utils/cn'
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react'

export type ToastType = 'success' | 'error'
export type ToastVariant = 'fill' | 'outline'

export interface TeeToastProps {
  /**
   * Toast message text
   */
  message: string

  /**
   * Toast type - determines color and icon
   */
  type?: ToastType

  /**
   * Toast variant - fill or outline
   */
  variant?: ToastVariant

  /**
   * Show leading icon
   */
  showIcon?: boolean

  /**
   * Custom leading icon
   */
  icon?: React.ReactNode

  /**
   * Show dismiss icon
   */
  showDismiss?: boolean

  /**
   * Dismiss button click handler
   */
  onDismiss?: () => void

  /**
   * Additional CSS classes
   */
  className?: string
}

export const TeeToast = React.forwardRef<HTMLDivElement, TeeToastProps>(
  (
    {
      message,
      type = 'success',
      variant = 'outline',
      showIcon = true,
      icon,
      showDismiss = true,
      onDismiss,
      className,
    },
    ref
  ) => {
    const isSuccess = type === 'success'
    const isFill = variant === 'fill'
    const isOutline = variant === 'outline'

    const bgClass = isFill
      ? isSuccess
        ? 'bg-[var(--background-toast-message-success,#15803d)]'
        : 'bg-[var(--background-toast-message-error,#923430)]'
      : 'bg-[var(--background-toast-message-default,white)]'

    const borderClass =
      isOutline &&
      (isSuccess
        ? 'border border-[var(--border-toast-message-success,#15803d)] border-solid'
        : 'border border-[var(--border-toast-message-error,#923430)] border-solid')

    const textClass = isFill
      ? 'text-[var(--text-toast-message-primary,white)]'
      : isSuccess
        ? 'text-[var(--text-toast-message-success,#15803d)]'
        : 'text-[var(--text-toast-message-error,#923430)]'

    const iconColorClass = isFill
      ? 'text-white'
      : isSuccess
        ? 'text-[var(--text-toast-message-success,#15803d)]'
        : 'text-[var(--text-toast-message-error,#923430)]'

    const dismissColorClass = isFill ? 'text-white' : 'text-[#6C756D]'

    const defaultIcon = isSuccess ? (
      <IconCheck size={20} className={iconColorClass} />
    ) : (
      <IconAlertCircle size={20} className={iconColorClass} />
    )

    return (
      <div
        ref={ref}
        className={cn(
          'flex gap-2 items-center p-4 rounded-[var(--spacing-border-radius-xs,6px)]',
          bgClass,
          borderClass,
          className
        )}
      >
        {showIcon && (icon || defaultIcon)}
        <p className={cn('font-body-s text-body-s flex-1', textClass)}>
          {message}
        </p>
        {showDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-0 bg-transparent border-0 cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Dismiss"
          >
            <IconX size={20} className={dismissColorClass} />
          </button>
        )}
      </div>
    )
  }
)

TeeToast.displayName = 'TeeToast'

export { TeeToast as default }
